'use client';

import { computePosition, flip, shift } from '@floating-ui/dom';
import { ReactRenderer, posToDOMRect } from '@tiptap/react';

import { MentionList } from './mention-list';

const updatePosition = (editor, element) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to),
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    Object.assign(element.style, {
      width: 'max-content',
      position: strategy,
      left: `${x}px`,
      top: `${y}px`,
    });
  });
};

export default function createMentionSuggestion({ getData }) {
  return {
    items: ({ query }) => {
      const data = getData();
      return data
        .filter((item) => item?.key.toLowerCase().startsWith(query.toLowerCase()))
        .slice(0, 5);
    },
    render: () => {
      let reactRenderer;

      return {
        onStart: (props) => {
          if (!props.clientRect) return;

          reactRenderer = new ReactRenderer(MentionList, {
            props: {
              ...props,
              command: (item) => {
                props.command({
                  id: item.key,
                });
              },
            },
            editor: props.editor,
          });

          reactRenderer.element.style.position = 'absolute';
          document.body.appendChild(reactRenderer.element);
          updatePosition(props.editor, reactRenderer.element);

          const handleEditorBlur = () => {
            reactRenderer?.destroy();
            reactRenderer?.element?.remove();
          };

          props.editor.on('blur', handleEditorBlur);
          reactRenderer.handleEditorBlur = handleEditorBlur;
        },

        onUpdate(props) {
          if (!reactRenderer) return;
          reactRenderer.updateProps({
            ...props,
            command: (item) => {
              props.command({
                id: item.key,
              });
            },
          });

          if (!props.clientRect) return;
          updatePosition(props.editor, reactRenderer.element);
        },

        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            reactRenderer?.destroy();
            reactRenderer?.element?.remove();
            return true;
          }

          return reactRenderer?.ref?.onKeyDown?.(props) ?? false;
        },

        onExit() {
          if (reactRenderer?.handleEditorBlur) {
            reactRenderer.editor?.off('blur', reactRenderer.handleEditorBlur);
          }
          reactRenderer?.destroy();
          reactRenderer?.element?.remove();
        },
      };
    },
  };
}
